import { EvidenceDraftStatus, EvidenceOrderSourceKind, EvidenceOrderType } from "./evidence";

// Mirrors BANK_REASON_CODES in apps/api/modules/evidence/types/evidence.ts.
export const BANK_REASON_CODES = [
  'FRAUDULENT', 'PRODUCT_NOT_RECEIVED', 'PRODUCT_UNACCEPTABLE', 'SUBSCRIPTION_CANCELED',
  'CREDIT_NOT_PROCESSED', 'DUPLICATE', 'GENERAL',
] as const;
export type BankReasonCode = typeof BANK_REASON_CODES[number];

// Mirrors EvidenceBankCaseRow in apps/api/modules/evidence/types/evidence.ts.
export interface BankCase {
  evidence_bank_case_id: number;
  company_id: number;
  order_source: EvidenceOrderSourceKind;
  order_account_id: number;
  order_key: string;
  order_name: string;
  bank_name: string | null;
  dispute_reference: string;
  merchant_id: string | null;
  reason_code: BankReasonCode;
  reason_text: string | null;
  /** Integer cents. */
  amount: number;
  currency: string;
  /** 'YYYY-MM-DD'. */
  disputed_on: string;
  respond_by: string | null;
  created_at: string;
  updated_at: string;
}

// Mirrors EvidenceBankCaseListRow in apps/api/repositories/evidenceBankCasesRepository.ts.
export interface BankCaseListRow extends BankCase {
  company_name: string;
  draft_status: EvidenceDraftStatus | null;
}

// Mirrors OrderCandidate / OrderSearchResult / OrderSearchInput in apps/api/modules/evidence/types/evidence.ts.
export interface OrderCandidate {
  source: EvidenceOrderSourceKind;
  accountId: number;
  orderKey: string;
  orderName: string;
  placedOn: string | null;
  amount: number | null;
  currency: string | null;
  orderType?: EvidenceOrderType;
  cardBrand?: string;
  cardLast4?: string;
  customerEmail?: string;
  bankName?: string;
  merchantId?: string;
  matchedOn: 'card' | 'auth_code' | 'amount_and_date' | 'order_number';
}

export interface OrderSearchResult {
  candidates: OrderCandidate[];
  truncated: boolean;
  unavailable: EvidenceOrderSourceKind[];
  incomplete: string[];
}

/** What the agent typed; the bank builder adds the company, a dispute names its own. */
export type OrderSearchQuery =
  | { by: 'payment'; amount: string; paidOn: string; cardLast4?: string; cardBin?: string; authCode?: string }
  | { by: 'order'; orderNumber: string };

export type OrderSearchBody = OrderSearchQuery & { companyId: number };

/** PUT /disputes/:id/evidence-order. */
export interface EvidenceOrderBody {
  order: { source: EvidenceOrderSourceKind; accountId: number; orderKey: string; orderName: string };
}

/** The order of a case, as POST takes it and PUT .../order changes it. */
export interface BankCaseOrderBody {
  order: { source: EvidenceOrderSourceKind; accountId: number; orderKey: string; orderName: string };
  orderType: EvidenceOrderType;
}

/** The facts form as the agent types it: every field a string, an untouched one empty. */
export interface BankFactsValues {
  bankName: string;
  disputeReference: string;
  merchantId: string;
  reasonCode: BankReasonCode;
  reasonText: string;
  amount: string;
  currency: string;
  disputedOn: string;
  respondBy: string;
}

/** What POST and PATCH take: an emptied optional field travels as null. */
export interface BankFactsBody {
  bankName: string | null;
  disputeReference: string;
  merchantId: string | null;
  reasonCode: BankReasonCode;
  reasonText: string | null;
  amount: string;
  currency: string;
  disputedOn: string;
  respondBy: string | null;
}
